import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, appointmentsApi } from "../../../services";
import { DailyBalance as DailyBalanceType, Appointment } from "../../../types";
import { getTodayInBrazil, formatDateForDisplay, formatTime } from "../../../utils/dateUtils";
import { Input, Loading, ErrorState, ActionButton } from "../../shared";
import { Icons } from "../../shared/Icons";
import { useCompany } from "../../../contexts/CompanyContext";
import { authService } from "../../../services";
import { UpdatePaymentModal } from "../AdminAppointments/UpdatePaymentModal";
import { BalanceBreakdown } from "./BalanceBreakdown";
import { BalanceByServiceCard } from "./BalanceByServiceCard";
import { BalanceUnpaidRow } from "./BalanceUnpaidRow";
import { formatBalanceCurrency } from "./balanceShared";

interface DailyBalanceProps {
  professionalId?: number | null;
}

export const DailyBalance: React.FC<DailyBalanceProps> = ({ professionalId: propProfessionalId }) => {
  const navigate = useNavigate();
  const { company } = useCompany();
  const userData = authService.getUserData();
  const userType = userData?.userType;
  const isProfessional = userType === "professional";
  const loggedInProfessionalId = isProfessional ? userData?.id : null;

  const today = getTodayInBrazil();
  const [selectedDate, setSelectedDate] = useState(today);
  const [balance, setBalance] = useState<DailyBalanceType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appointmentsWithoutPayment, setAppointmentsWithoutPayment] = useState<Appointment[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [appointmentForPayment, setAppointmentForPayment] = useState<Appointment | null>(null);

  const professionalId = isProfessional ? loggedInProfessionalId : propProfessionalId;

  const handleViewAppointments = () => {
    sessionStorage.setItem("adminAppointmentsSelectedDate", selectedDate);
    navigate("/admin/appointments");
  };

  const fetchDailyBalance = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const professionalIdToUse = company?.category !== "car" ? (professionalId ?? undefined) : undefined;
      const response = await adminApi.getDailyBalance(selectedDate, professionalIdToUse);

      if (response.success && response.data) {
        setBalance(response.data);
      } else {
        setError(response.error || "Erro ao carregar saldo diário");
      }
    } catch (err) {
      console.error("Error fetching daily balance:", err);
      setError("Erro ao carregar saldo diário");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, company?.category, professionalId]);

  const fetchAppointmentsWithoutPayment = useCallback(async () => {
    if (!company) return;

    setLoadingAppointments(true);
    try {
      let response;

      if (company.category === "car") {
        response = await appointmentsApi.getAppointmentsByCompanyAndDate(selectedDate);
      } else {
        if (!professionalId) {
          setAppointmentsWithoutPayment([]);
          setLoadingAppointments(false);
          return;
        }
        response = await appointmentsApi.getAppointmentsByProfessionalAndDate(professionalId, selectedDate);
      }

      if (response.success && response.data) {
        const withoutPayment = response.data.filter(
          (appointment) => !appointment.payment || appointment.payment === null
        );
        setAppointmentsWithoutPayment(withoutPayment);
      } else {
        setAppointmentsWithoutPayment([]);
      }
    } catch (err) {
      console.error("Error fetching appointments without payment:", err);
      setAppointmentsWithoutPayment([]);
    } finally {
      setLoadingAppointments(false);
    }
  }, [company, selectedDate, professionalId]);

  useEffect(() => {
    if (company) {
      fetchDailyBalance();
      fetchAppointmentsWithoutPayment();
    }
  }, [company, fetchDailyBalance, fetchAppointmentsWithoutPayment]);

  return (
    <>
      <div className="bg-white dark:!bg-slate-800 rounded-xl sm:rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-3 sm:p-4 md:p-6">
      <div className="mb-4 sm:mb-6">
        <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-800 dark:text-slate-100 mb-3 sm:mb-4">
          Saldo Diário
        </h3>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-end">
          <div className="flex-1 sm:flex-initial sm:min-w-[200px]">
            <Input
              label="Selecione a data"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full sm:w-auto"
            />
          </div>
          <div className="text-xs sm:text-sm text-gray-600 dark:text-slate-400">{formatDateForDisplay(selectedDate)}</div>
          <div className="flex justify-end sm:justify-end w-full sm:w-auto sm:ml-auto">
            <ActionButton
              variant="primary"
              size="md"
              icon={<Icons.Calendar sx={{ fontSize: 20 }} />}
              onClick={handleViewAppointments}
              className="w-full sm:w-auto"
            >
              Ver Agendamentos
            </ActionButton>
          </div>
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorState message={error} />
      ) : balance ? (
        <BalanceBreakdown
          balance={balance}
          totalPrimaryLabel="Total do Dia"
          emptyPeriodMessage="Nenhum pagamento registrado para esta data"
          unpaidCount={appointmentsWithoutPayment.length}
          unpaidRows={appointmentsWithoutPayment.map((appointment) => (
            <BalanceUnpaidRow
              key={appointment.id}
              onPay={() => {
                setAppointmentForPayment(appointment);
                setPaymentModalOpen(true);
              }}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                  {appointment.client?.name || "Cliente"}
                </span>
                {appointment.startTime && (
                  <span className="text-xs text-gray-600 dark:text-slate-400">{formatTime(appointment.startTime)}</span>
                )}
              </div>
              <p className="text-xs text-gray-600 dark:text-slate-400 truncate">{appointment.service?.name || "Serviço"}</p>
              {appointment.price && (
                <p className="text-xs sm:text-sm font-semibold text-red-700 dark:text-red-400 mt-1">
                  {formatBalanceCurrency(parseFloat(appointment.price))}
                </p>
              )}
            </BalanceUnpaidRow>
          ))}
          showAllPaidMessage={
            !loadingAppointments && appointmentsWithoutPayment.length === 0 && balance.total > 0
          }
        />
      ) : null}

      {appointmentForPayment && (
        <UpdatePaymentModal
          isOpen={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
          }}
          onSuccess={(updatedAppointment) => {
            if (updatedAppointment) {
              setAppointmentsWithoutPayment((prev) => prev.filter((apt) => apt.id !== updatedAppointment.id));
              fetchDailyBalance();
            }
            setPaymentModalOpen(false);
            setAppointmentForPayment(null);
          }}
          appointment={appointmentForPayment}
        />
      )}
      </div>
      {!loading && !error && balance ? (
        <BalanceByServiceCard items={balance.byService ?? []} />
      ) : null}
    </>
  );
};
